// utils/navigation.ts
import { toast } from 'react-toastify';
import i18n from 'i18next';
import type { NavigateFunction } from 'react-router-dom';

/** pill 注意事項のスコープ（予約ページ ReservationPage の PillScope と一致させる） */
export type PillNoticeScope = 'pill_all' | 'pill_low' | 'pill_shift' | 'pill_after';

/**
 * 予約ページへ渡すオプション（react-router の location.state 経由）
 */
export type ReserveNavOptions = {
  /** 事前選択する診療科目（例: 'pill' | 'pill_low' | 'pill_shift' | 'pill_after' | 'aga_female' など） */
  preselectService?: string;
  /** 「注意事項を確認済み」を事前にオンにするか */
  preAgreed?: boolean;
  /** 遷移元：nav（ナビの診療予約） or service（各診療紹介ページ） */
  from?: 'nav' | 'service';
  /** react-router navigate の replace オプション */
  replace?: boolean;
  /**
   * pill 専用：注意事項のスコープ
   * - 総覧からの遷移… 'pill_all'
   * - 低用量ページ… 'pill_low'
   * - 月経移動ページ… 'pill_shift'
   * - アフターピルページ… 'pill_after'
   */
  pillNoticeScope?: PillNoticeScope;
  /** 追加で渡したい任意の state（必要であれば使用） */
  state?: Record<string, unknown>;
};

/**
 * 予約ページへ遷移（IP 制限チェック + state を同時に渡す）
 * - URL クエリは付与せず、location.state のみで連携します（現行実装に合わせて最小変更）
 * - もし共有用にクエリ（?svc=...）も必要になった場合は、navigate の第一引数をオブジェクトにして search を追加してください
 */
export function goReserve(
  navigate: NavigateFunction,
  ipAllowed: boolean,
  opts?: ReserveNavOptions
): void {
  // IP 制限（NG の場合はトースト表示のみで中断）
  if (!ipAllowed) {
    toast.error(i18n.t('auth.ip_restricted'));
    return;
  }

  const {
    preselectService,
    preAgreed,
    from = 'nav',
    replace = false,
    pillNoticeScope, 
    state = {},
  } = opts ?? {};

  // 予約ページへ遷移：予約ページ側は location.state から各種プリセットを受け取る
  navigate('/reserve', {
    replace,
    state: {
      preselectService,
      preAgreed: !!preAgreed,
      from,
      pillNoticeScope, 
      ...state,        // 既存の任意 state があれば最後にマージ
    },
  });
}

/**
 * スマート戻る（履歴がなければフォールバックへ）
 * - SPA のヒストリーに戻り先がない場合を考慮してフォールバックへ置換遷移
 */
export function goBackSmart(
  navigate: NavigateFunction,
  fallback: string = '/faq'
): void {
  try {
    const idx =
      (window.history?.state && (window.history.state as any).idx) ?? 0;
    if (typeof idx === 'number' && idx > 0) {
      navigate(-1);
    } else {
      navigate(fallback, { replace: true });
    }
  } catch {
    navigate(fallback, { replace: true });
  }
}
